import { Button, Card, Dialog, EmptyState, Input, Menu, MenuItem, MenuLabel, Select } from "@kontaktfix/ui";
import {
  AddressBookIcon,
  DownloadSimpleIcon,
  FolderMinusIcon,
  FolderPlusIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  TrashIcon,
  UploadSimpleIcon,
} from "@phosphor-icons/react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ContactSheet } from "@/components/contact-sheet";
import {
  ContactsTable,
  noSelection,
  Pagination,
  SelectAllBanner,
  type Selection,
  selectionBody,
  selectionCount,
} from "@/components/contacts-table";
import { Page, PageHeader } from "@/components/page";
import { api } from "@/lib/api";
import { toastError } from "@/lib/errors";
import { formatNumber } from "@/lib/i18n";
import { useCanChangeContacts, useContacts, useInvalidateData, useLists, useMe } from "@/lib/queries";

const PAGE_SIZE = 50;

export function useDebounced<T>(value: T, ms = 250): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handle = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(handle);
  }, [value, ms]);
  return debounced;
}

export function ExportMenu({ listId }: { listId?: string }) {
  const { t } = useTranslation();
  const href = (format: string) => `/api/export?format=${format}${listId ? `&listId=${listId}` : ""}`;
  return (
    <Menu
      trigger={
        <Button>
          <DownloadSimpleIcon />
          {t("common.export")}
        </Button>
      }
    >
      <MenuItem onSelect={() => window.location.assign(href("xlsx"))}>Excel (.xlsx)</MenuItem>
      <MenuItem onSelect={() => window.location.assign(href("csv"))}>CSV (.csv)</MenuItem>
      <MenuItem onSelect={() => window.location.assign(href("vcf"))}>vCard (.vcf)</MenuItem>
    </Menu>
  );
}

export function ContactsPage() {
  const { t } = useTranslation();
  const search = useSearch({ from: "/app/contacts" });
  const navigate = useNavigate({ from: "/contacts" });
  const { data: me } = useMe();
  const [query, setQuery] = useState(search.q ?? "");
  const debounced = useDebounced(query);
  const page = search.page ?? 1;
  const { data, isLoading, isFetching } = useContacts({
    q: debounced,
    listId: search.listId,
    page,
    pageSize: PAGE_SIZE,
  });
  const { data: lists = [] } = useLists();
  const invalidate = useInvalidateData();
  const [selected, setSelected] = useState<Selection>(noSelection);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if ((search.q ?? "") !== debounced)
      void navigate({ search: (s) => ({ ...s, q: debounced || undefined, page: undefined }), replace: true });
  }, [debounced, navigate, search.q]);
  // "All matching" belongs to one filter; a new search or list starts over.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset on filter change only
  useEffect(() => setSelected((s) => (s.all ? noSelection : s)), [debounced, search.listId]);

  const openContact = (id: string | undefined) => navigate({ search: (s) => ({ ...s, contact: id }) });
  const editableLists = lists.filter((l) => l.canEdit);
  const isAdmin = me?.role === "admin";
  const canChange = useCanChangeContacts();
  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const count = selectionCount(selected, total);
  const body = () => selectionBody(selected, { q: debounced, listId: search.listId }, total);

  const bulk = async (action: "add" | "remove", listId: string) => {
    try {
      const { changed } = await api.post<{ changed: number }>(`/api/contacts/bulk/${action}-list`, {
        ...body(),
        listId,
      });
      toast.success(
        t(action === "add" ? "contacts.addedToList" : "contacts.removedFromList", {
          count: changed,
          n: formatNumber(changed),
        }),
      );
      setSelected(noSelection);
      await invalidate();
    } catch (err) {
      toastError(err);
      // e.g. selection_changed: show the current numbers.
      await invalidate();
    }
  };
  const bulkDelete = async () => {
    try {
      await api.post("/api/contacts/bulk/delete", body());
      toast.success(t("contacts.deleted"));
      setSelected(noSelection);
      setConfirmDelete(false);
      await invalidate();
    } catch (err) {
      toastError(err);
      setConfirmDelete(false);
      await invalidate();
    }
  };

  const filtered = Boolean(debounced || search.listId);

  return (
    <Page wide>
      <PageHeader
        title={t("contacts.title")}
        description={t("contacts.description")}
        actions={
          <>
            {canChange && (
              <Button asChild>
                <Link to="/import">
                  <UploadSimpleIcon />
                  {t("common.import")}
                </Link>
              </Button>
            )}
            <ExportMenu listId={search.listId} />
            {canChange && (
              <Button variant="primary" onClick={() => openContact("new")}>
                <PlusIcon />
                {t("contacts.new")}
              </Button>
            )}
          </>
        }
      />

      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center">
        <Input
          icon={<MagnifyingGlassIcon />}
          className="sm:max-w-sm sm:flex-1"
          placeholder={t("contacts.search")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <Select
          className="sm:w-56"
          value={search.listId ?? ""}
          onChange={(e) =>
            navigate({ search: (s) => ({ ...s, listId: e.target.value || undefined, page: undefined }) })
          }
        >
          <option value="">{t("contacts.allLists")}</option>
          {lists.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name} ({l.contactCount})
            </option>
          ))}
        </Select>
        {count > 0 && (
          <div className="animate-fade-in flex items-center gap-2 sm:ml-auto">
            <span className="text-[12.5px] text-fg-muted">
              {t("contacts.selected", { n: formatNumber(count) })}
            </span>
            {canChange && editableLists.length > 0 && (
              <Menu
                trigger={
                  <Button size="sm">
                    <FolderPlusIcon />
                    {t("contacts.addToList")}
                  </Button>
                }
              >
                <MenuLabel>{t("contacts.addToList")}</MenuLabel>
                {editableLists.map((l) => (
                  <MenuItem key={l.id} onSelect={() => bulk("add", l.id)}>
                    {l.name}
                  </MenuItem>
                ))}
              </Menu>
            )}
            {canChange && editableLists.length > 0 && (
              <Menu
                trigger={
                  <Button size="sm">
                    <FolderMinusIcon />
                    {t("contacts.removeFromList")}
                  </Button>
                }
              >
                <MenuLabel>{t("contacts.removeFromList")}</MenuLabel>
                {editableLists.map((l) => (
                  <MenuItem key={l.id} onSelect={() => bulk("remove", l.id)}>
                    {l.name}
                  </MenuItem>
                ))}
              </Menu>
            )}
            {isAdmin && (
              <Button size="sm" variant="danger-ghost" onClick={() => setConfirmDelete(true)}>
                <TrashIcon />
                {t("contacts.deleteSelected")}
              </Button>
            )}
          </div>
        )}
      </div>

      <Card className={isFetching && !isLoading ? "opacity-80 transition-opacity" : undefined}>
        {!isLoading && !items.length ? (
          filtered ? (
            <EmptyState icon={<MagnifyingGlassIcon />} title={t("contacts.noResults")} />
          ) : (
            <EmptyState
              icon={<AddressBookIcon />}
              title={t("contacts.empty")}
              description={t("contacts.emptyText")}
              action={
                canChange && (
                  <div className="flex gap-2">
                    <Button asChild>
                      <Link to="/import">
                        <UploadSimpleIcon />
                        {t("common.import")}
                      </Link>
                    </Button>
                    <Button variant="primary" onClick={() => openContact("new")}>
                      <PlusIcon />
                      {t("contacts.new")}
                    </Button>
                  </div>
                )
              }
            />
          )
        ) : (
          <>
            <SelectAllBanner items={items} total={total} selected={selected} onSelectedChange={setSelected} />
            <ContactsTable
              items={items}
              lists={lists}
              loading={isLoading}
              selected={selected}
              onSelectedChange={setSelected}
              onOpen={(id) => openContact(id)}
            />
            <Pagination
              page={page}
              pageSize={PAGE_SIZE}
              total={total}
              onPage={(p) => navigate({ search: (s) => ({ ...s, page: p }) })}
            />
          </>
        )}
      </Card>

      <ContactSheet
        contactId={search.contact ?? null}
        defaultListIds={
          search.listId ? [search.listId] : editableLists.length === 1 ? [editableLists[0]!.id] : []
        }
        onClose={() => openContact(undefined)}
      />
      <Dialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={t("contacts.deleteTitle", { count, n: formatNumber(count) })}
        description={t("contacts.deleteText")}
        footer={
          <>
            <Button onClick={() => setConfirmDelete(false)}>{t("common.cancel")}</Button>
            <Button variant="danger" onClick={bulkDelete}>
              {t("common.delete")}
            </Button>
          </>
        }
      />
    </Page>
  );
}
